import { apiErrorMessage } from '@agos/api-client'
import {
  getGetHikvisionConfigQueryKey,
  useGetHikvisionConfig,
  usePostHikvisionConfig,
  usePostHikvisionSync,
  usePostHikvisionTest,
} from '@agos/api-client/devices'
import { T, formatCount, formatDateTime, tt, useAuth } from '@agos/shared'
import { PageHeader, Spinner, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Cpu,
  Info,
  KeyRound,
  RefreshCw,
  Save,
  Wifi,
} from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  SUGGESTED,
  carriesToken,
  configOf,
  formOf,
  lastSyncLabel,
  maySave,
  mayTest,
  missingOf,
  payloadOf,
  pollIntervalIgnored,
  pollIntervalNote,
  problemMessage,
  problemsOf,
  setupMessage,
  setupState,
  testPayloadOf,
  webhookSteps,
  webhookUrl,
  type FieldProblem,
  type HikvisionForm,
} from './hikvision-config'

/**
 * `/hikvision` — the attendance terminal, over ISAPI.
 *
 * **FB-155.** The polling interval is stored, bounded 10–3 600 and ignored. `hikvisionPolling()` is
 * `@Scheduled(fixedDelay = 60_000)` and `sync()` never reads `poll_interval` — legacy's footer printed the
 * stored number as the cadence in force.
 *
 * **FB-156.** The webhook URL it told you to paste into the device was wrong twice: the path lacked the `/v1`
 * the backend mounts, and the host came from the manager's origin with its port stripped by a regular
 * expression. And `device_token`, the secret that webhook is authenticated with, was fetched and shown in a
 * plain text box while the device password beside it was correctly withheld.
 *
 * `hikvision_config` holds no row on the restored data, so the form starts **empty** — legacy seeds
 * `admin` / `80` / `60` into the boxes, which makes a suggestion read as a stored value.
 *
 * Ported from legacy `pages/hikvision-settings.tsx` (362 lines).
 */

export function HikvisionSettingsPage() {
  const query = useGetHikvisionConfig()

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return <HikvisionEditor config={configOf(query.data)} />
}

const FIELD =
  'w-full px-3 py-2 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60'

function HikvisionEditor({ config }: { config: ReturnType<typeof configOf> }) {
  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<HikvisionForm>(() => formOf(config))
  const [testResult, setTestResult] = useState<{ ok: boolean; label: string } | null>(null)

  // `/hikvision/*` is one of the four sections an `admin` may write as well as read.
  const writable = mayWrite('hikvision', user?.systemRole)
  const readOnly = readOnlyReason('hikvision', user?.systemRole)

  const problems = problemsOf(form)
  const missing = missingOf(form)
  const state = setupState(config)
  const url = webhookUrl(typeof window === 'undefined' ? '' : window.location.origin)

  const save = usePostHikvisionConfig({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetHikvisionConfigQueryKey() })
        setForm((current) => ({ ...current, password: '', deviceToken: '' }))
        toast({ title: tt('Konfigurácia uložená') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const test = usePostHikvisionTest({
    mutation: {
      onSuccess: (answer) => {
        const row = answer as { ok?: unknown; deviceName?: unknown; error?: unknown } | undefined
        const ok = row?.ok === true
        setTestResult({
          ok,
          label: ok
            ? typeof row?.deviceName === 'string' && row.deviceName !== ''
              ? row.deviceName
              : tt('Zariadenie odpovedalo')
            : typeof row?.error === 'string' && row.error !== ''
              ? row.error
              : tt('Zariadenie neodpovedalo'),
        })
      },
      onError: (caught) => setTestResult({ ok: false, label: apiErrorMessage(
                                                   caught,
                                                   tt('Zariadenie neodpovedalo'),
                                                 ) }),
    },
  })

  const sync = usePostHikvisionSync({
    mutation: {
      onSuccess: (answer) => {
        void queryClient.invalidateQueries({ queryKey: getGetHikvisionConfigQueryKey() })
        const count = (answer as { count?: unknown } | undefined)?.count
        toast({
          title: tt('Synchronizácia dokončená'),
          description:
            typeof count === 'number'
              ? `${tt('Importovaných udalostí')}: ${formatCount(count)}`
              : tt('Server nepovedal, koľko udalostí prišlo.'),
        })
      },
      onError: (error: unknown) =>
        toast({
          variant: 'destructive',
          title: tt('Synchronizácia zlyhala'),
          description: apiErrorMessage(error, tt('Zariadenie neodpovedalo alebo odmietlo prihlásenie.')),
        }),
    },
  })

  const set = <K extends keyof HikvisionForm>(key: K, value: HikvisionForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Hikvision zariadenie')}
        description={tt('Terminál dochádzky cez ISAPI')}
        icon={<Cpu className="w-5 h-5" />}
        actions={
          state === 'configured' ? (
            <button
              type="button"
              data-sync
              disabled={!writable || sync.isPending}
              onClick={() => sync.mutate()}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-card text-sm text-muted-foreground disabled:opacity-50"
            >
              {sync.isPending ? <Spinner /> : <RefreshCw className="w-4 h-4" />}{' '}
              <T>Synchronizovať teraz</T>
            </button>
          ) : undefined
        }
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      <div
        className={cn(
          'rounded-2xl border px-4 py-3 flex items-start gap-3',
          state === 'configured'
            ? 'border-emerald-500/40 bg-emerald-500/5'
            : 'border-amber-500/40 bg-amber-500/5',
        )}
        data-setup={state}
      >
        {state === 'configured' ? (
          <Wifi className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
        ) : (
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{setupMessage(state)}</p>
          {state === 'configured' && (
            <p className="text-xs text-muted-foreground mt-1">
              <T>Posledná synchronizácia</T>:{' '}
              <span data-last-sync>
                {config?.lastSyncAt === null || config === null
                  ? lastSyncLabel(config)
                  : formatDateTime(config.lastSyncAt)}
              </span>{' '}
              · <T>udalostí</T>: <span data-last-count>{formatCount(config?.lastSyncCount ?? 0)}</span>
            </p>
          )}
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          <T>Pripojenie</T>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field
            className="sm:col-span-2"
            label={tt('IP adresa alebo názov')}
            problem={problems.includes('ip') ? 'ip' : null}
            hint={missing.includes('ip') ? tt('napríklad 192.168.1.100') : ''}
          >
            <input
              aria-label={tt('IP adresa alebo názov')}
              className={FIELD}
              disabled={!writable}
              value={form.ip}
              onChange={(event) => set('ip', event.target.value)}
            />
          </Field>
          <Field
            label={tt('Port')}
            problem={problems.includes('port') ? 'port' : null}
            hint={missing.includes('port') ? `${tt('zvyčajne')} ${SUGGESTED.port}` : ''}
          >
            <input
              aria-label={tt('Port')}
              className={FIELD}
              disabled={!writable}
              value={form.port}
              onChange={(event) => set('port', event.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label={tt('Prihlasovacie meno')}
            problem={null}
            hint={missing.includes('username') ? `${tt('zvyčajne')} ${SUGGESTED.username}` : ''}
          >
            <input
              aria-label={tt('Prihlasovacie meno')}
              className={FIELD}
              disabled={!writable}
              value={form.username}
              onChange={(event) => set('username', event.target.value)}
            />
          </Field>
          <Field
            label={tt('Heslo zariadenia')}
            problem={null}
            hint={
              state === 'configured'
                ? tt('uložené - nechajte prázdne, ak ho nemeníte')
                : tt('pri prvom uložení je povinné')
            }
          >
            <input
              type="password"
              aria-label={tt('Heslo zariadenia')}
              className={FIELD}
              autoComplete="off"
              disabled={!writable}
              value={form.password}
              onChange={(event) => set('password', event.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* FB-156. The token is the webhook's shared secret; legacy printed it in a text box. */}
          <Field
            label="Device Token"
            problem={null}
            hint={
              carriesToken(config)
                ? tt('uložený - nechajte prázdne, ak ho nemeníte')
                : tt('nepovinný - overuje push udalosti zo zariadenia')
            }
          >
            <input
              type="password"
              aria-label="Device Token"
              className={FIELD}
              autoComplete="off"
              disabled={!writable}
              value={form.deviceToken}
              onChange={(event) => set('deviceToken', event.target.value)}
            />
            <p
              className="text-[11px] text-muted-foreground flex items-center gap-1"
              data-token={carriesToken(config) ? 'stored' : 'absent'}
            >
              <KeyRound className="w-3 h-3 shrink-0" />
              {carriesToken(config) ? (
                <T>Token je uložený. Táto obrazovka ho nezobrazuje.</T>
              ) : (
                <T>Token nie je uložený, push udalosti sa neoverujú.</T>
              )}
            </p>
          </Field>
          {/* FB-155. Stored, bounded, and not what the job uses. */}
          <Field
            label={tt('Polling interval (sekundy)')}
            problem={problems.includes('poll') ? 'poll' : null}
            hint=""
          >
            <input
              aria-label={tt('Polling interval (sekundy)')}
              className={FIELD}
              disabled={!writable}
              value={form.pollInterval}
              onChange={(event) => set('pollInterval', event.target.value)}
            />
            <p
              className={cn('text-[11px]', pollIntervalIgnored(form) ? 'text-amber-500' : 'text-muted-foreground')}
              data-poll-note={pollIntervalIgnored(form) ? 'ignored' : 'matches'}
            >
              {pollIntervalNote(form)}
            </p>
          </Field>
        </div>

        {testResult !== null && (
          <p
            className={cn(
              'flex items-center gap-2 p-3 rounded-xl text-sm',
              testResult.ok
                ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                : 'bg-red-500/10 text-red-500 border border-red-500/20',
            )}
            data-test-result={testResult.ok ? 'ok' : 'failed'}
          >
            {testResult.ok ? (
              <CheckCircle2 className="w-4 h-4 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 shrink-0" />
            )}
            {testResult.label}
          </p>
        )}

        <div className="flex flex-wrap gap-3 pt-1">
          <button
            type="button"
            data-test
            disabled={!writable || test.isPending || !mayTest(form, config)}
            onClick={() => test.mutate({ data: { ...testPayloadOf(form) } })}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-muted text-sm font-medium disabled:opacity-50"
          >
            {test.isPending ? <Spinner /> : <Wifi className="w-4 h-4" />} <T>Test pripojenia</T>
          </button>
          <button
            type="button"
            data-save
            disabled={!writable || save.isPending || !maySave(form, config)}
            onClick={() => save.mutate({ data: { ...payloadOf(form) } })}
            className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {save.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť konfiguráciu</T>
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <Info className="w-4 h-4 text-primary" /> <T>Push mód na zariadení</T>
        </h2>
        <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal list-inside">
          {webhookSteps().map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <div className="flex items-center gap-2 bg-background border border-border rounded-lg px-3 py-2.5">
          <code className="text-xs text-primary flex-1 break-all" data-webhook-url>
            {url}
          </code>
          <button
            type="button"
            data-copy-webhook
            aria-label={tt('Kopírovať')}
            onClick={() => {
              void navigator.clipboard
                .writeText(url)
                .then(() => toast({ title: tt('URL skopírovaná') }))
                .catch(() => toast({ variant: 'destructive', title: tt('Kopírovanie sa nepodarilo') }))
            }}
            className="shrink-0 p-1 rounded text-muted-foreground hover:text-foreground"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground border-t border-border pt-3" data-both-modes>
          <T>
            Oba módy fungujú súbežne: zariadenie posiela udalosti samo a server ich raz za minútu aj sám
            sťahuje.
          </T>
        </p>
      </div>
    </div>
  )
}

function Field({
  label,
  problem,
  hint,
  className,
  children,
}: {
  label: string
  problem: FieldProblem
  hint: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      {children}
      {problem !== null ? (
        <p className="text-[11px] text-red-500 mt-1" data-problem={problem}>
          {problemMessage(problem)}
        </p>
      ) : (
        hint !== '' && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>
      )}
    </div>
  )
}
